import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Truck, ArrowDown, ArrowUp, Trash2, ScanLine, Loader2, CheckCircle2, AlertTriangle, LogOut } from "lucide-react";
import { AttachmentPicker } from "@/components/AttachmentPicker";
import { fileSrc } from "@/lib/uploads";
import { ClearLogsButton } from "@/components/ClearLogsButton";
import { VehicleToolsCheck, VehicleDetailModal, toolsSummary } from "@/components/VehicleDetail";
import { formatDateTimeAR } from "@/lib/i18n";
import { ExportButtons } from "@/components/ExportButtons"; // Fix BS

export default function VigiladorVehicles() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "supervisor";
  const [clients, setClients] = useState([]);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ client_id: "", plate: "", description: "", direction: "entrada", reason: "", driver_name: "", driver_surname: "", driver_dni: "", is_company_vehicle: false, tools_check: {} });
  // Fix BN (2026-11-03): vehículos adentro con salida en un toque, y detalle completo de cada movimiento.
  const [inside, setInside] = useState([]);
  const [exitTools, setExitTools] = useState({});
  const [exitingId, setExitingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [platePhoto, setPlatePhoto] = useState([]);
  const [plateSource, setPlateSource] = useState("manual");
  const [ocrStatus, setOcrStatus] = useState(null); // null | "reading" | "ok" | "failed"
  const [busy, setBusy] = useState(false);
  const ocrUrlRef = useRef(null);

  const load = async () => {
    try {
      const [c, v] = await Promise.all([api.get("/clients"), api.get("/vehicles")]);
      setClients(c.data); setList(v.data);
    } catch (e) { toast.error(formatApiError(e)); }
    try { const { data } = await api.get("/vehicles/active"); setInside(data); } catch { setInside([]); }
  };

  const registerExit = async (v) => {
    setExitingId(v.id);
    try {
      await api.post(`/vehicles/${v.id}/exit`, { tools_check: v.is_company_vehicle ? (exitTools[v.id] || null) : null });
      toast.success(`Salida registrada: ${v.plate}`);
      setExitTools((t) => { const n = { ...t }; delete n[v.id]; return n; });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExitingId(null); }
  };
  useEffect(() => { load(); }, []);

  // Al adjuntar/cambiar la foto de patente, intentar leerla automáticamente.
  // Es solo una sugerencia: si tarda o falla, el campo queda editable igual.
  useEffect(() => {
    const url = platePhoto[0]?.url;
    if (!url || url === ocrUrlRef.current) return;
    ocrUrlRef.current = url;
    let cancelled = false;
    setOcrStatus("reading");
    api.post("/vehicles/plate-ocr", { url }, { timeout: 13000 })
      .then((r) => {
        if (cancelled) return;
        const plate = r.data?.plate;
        if (plate) {
          setForm((f) => ({ ...f, plate }));
          setPlateSource("ocr");
          setOcrStatus("ok");
        } else {
          setOcrStatus("failed");
        }
      })
      .catch(() => { if (!cancelled) setOcrStatus("failed"); });
    return () => { cancelled = true; };
  }, [platePhoto]);

  const removeLog = async (id) => {
    if (!window.confirm("¿Eliminar este movimiento? No se puede deshacer.")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Movimiento eliminado");
      setList((l) => l.filter((v) => v.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/vehicles", {
        ...form,
        tools_check: form.is_company_vehicle && Object.keys(form.tools_check).length ? form.tools_check : null,
        plate_photo_url: platePhoto[0]?.url || null, plate_source: plateSource,
      });
      toast.success("Movimiento registrado");
      setForm({ ...form, plate: "", description: "", reason: "", driver_name: "", driver_surname: "", driver_dni: "", is_company_vehicle: false, tools_check: {} });
      setPlatePhoto([]);
      setPlateSource("manual");
      setOcrStatus(null);
      ocrUrlRef.current = null;
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ padding: "8px 16px" }}>
      <Link to=".." style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 18px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Vehículos</h1>

      <div className="card" style={{ padding: 0, marginBottom: 18, overflow: "hidden" }} data-testid="veh-inside-list">
        <div style={{ padding: "12px 16px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", borderBottom: "1px solid var(--invict-bone-3)" }}>
          Adentro ahora · {inside.length}
        </div>
        {inside.length === 0 && <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: "var(--invict-text-muted)" }}>Ningún vehículo adentro.</div>}
        {inside.map((v) => (
          <div key={v.id} style={{ padding: "10px 16px", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`veh-inside-row-${v.id}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 13, fontFamily: "monospace", letterSpacing: "0.1em" }}>
                  {v.plate}{v.is_company_vehicle && <span className="badge" style={{ marginLeft: 6 }}>Empresa</span>}
                </div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
                  {v.client_name} · entró {formatDateTimeAR(v.created_at)}{v.description ? ` · ${v.description}` : ""}
                </div>
                {(v.driver_name || v.driver_surname) && <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Conductor: {[v.driver_surname, v.driver_name].filter(Boolean).join(", ")}</div>}
              </div>
              <button type="button" className="btn-primary" onClick={() => registerExit(v)} disabled={exitingId === v.id} data-testid={`veh-exit-${v.id}`}
                style={{ padding: "10px 14px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                {exitingId === v.id ? <Loader2 size={14} className="spin" /> : <LogOut size={14} />} Registrar salida
              </button>
            </div>
            {v.is_company_vehicle && (
              <div style={{ marginTop: 8 }}>
                <VehicleToolsCheck value={exitTools[v.id] || {}} onChange={(t) => setExitTools((all) => ({ ...all, [v.id]: t }))} testIdPrefix={`veh-exit-tools-${v.id}`} />
              </div>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="card" style={{ marginBottom: 18, padding: 16 }}>
        <div className="field"><label>Cliente *</label>
          <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="veh-client">
            <option value="">Elegir cliente...</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Patente *</label><input required value={form.plate} onChange={(e) => { setForm({ ...form, plate: e.target.value }); setPlateSource("manual"); }} placeholder="AB123CD" style={{ textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }} data-testid="veh-plate"/></div>
        <div className="field"><label>Foto de la patente</label>
          <AttachmentPicker value={platePhoto} onChange={(v) => { setPlatePhoto(v); if (!v.length) { setOcrStatus(null); ocrUrlRef.current = null; } }} accept="image" max={1} label="Foto patente"/>
          {ocrStatus === "reading" && (
            <div data-testid="veh-plate-ocr-status" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 12, color: "var(--invict-text-muted)", fontWeight: 700 }}>
              <Loader2 size={13} className="spin"/> Leyendo patente de la foto...
            </div>
          )}
          {ocrStatus === "ok" && (
            <div data-testid="veh-plate-ocr-status" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 12, color: "#16a34a", fontWeight: 700 }}>
              <CheckCircle2 size={13}/> Patente detectada automáticamente · revisá y corregí si hace falta
            </div>
          )}
          {ocrStatus === "failed" && (
            <div data-testid="veh-plate-ocr-status" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 12, color: "#BF3437", fontWeight: 700 }}>
              <AlertTriangle size={13}/> No se pudo leer la patente automáticamente, escribila a mano
            </div>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div className="field"><label>Nombre conductor</label><input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="veh-driver-name"/></div>
          <div className="field"><label>Apellido</label><input value={form.driver_surname} onChange={(e) => setForm({ ...form, driver_surname: e.target.value })} data-testid="veh-driver-surname"/></div>
        </div>
        <div className="field"><label>DNI conductor</label><input value={form.driver_dni} onChange={(e) => setForm({ ...form, driver_dni: e.target.value })} data-testid="veh-driver-dni"/></div>
        <div className="field"><label>Descripción del vehículo</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Camión Ford blanco / Reparto correo / etc." data-testid="veh-desc"/></div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, margin: "0 0 12px", cursor: "pointer" }}>
          <input type="checkbox" checked={form.is_company_vehicle} onChange={(e) => setForm({ ...form, is_company_vehicle: e.target.checked })} data-testid="veh-company"/>
          Vehículo de la empresa
        </label>
        {form.is_company_vehicle && form.direction === "salida" && (
          <div className="field"><label>Herramientas al egreso</label>
            <VehicleToolsCheck value={form.tools_check} onChange={(t) => setForm({ ...form, tools_check: t })} />
          </div>
        )}
        <div className="field"><label>Movimiento</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { v: "entrada", label: "Entrada", color: "#16a34a", Icon: ArrowDown },
              { v: "salida", label: "Salida", color: "#BF3437", Icon: ArrowUp },
            ].map(({ v, label, color, Icon }) => (
              <button key={v} type="button" onClick={() => setForm({ ...form, direction: v })}
                style={{ flex: 1, padding: "12px", border: `2px solid ${form.direction === v ? color : "var(--invict-bone-3)"}`, background: form.direction === v ? color : "white", color: form.direction === v ? "white" : "var(--invict-text-muted)", fontWeight: 800, fontSize: 12, letterSpacing: "0.05em", textTransform: "uppercase", borderRadius: 2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                data-testid={`veh-direction-${v}`}
              ><Icon size={14}/> {label}</button>
            ))}
          </div>
        </div>
        <div className="field"><label>Motivo</label><input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Entrega de mercadería / Visita técnica..."/></div>
        <button type="submit" className="btn-primary" disabled={busy} style={{ width: "100%", padding: 12 }} data-testid="veh-submit">{busy ? "Guardando..." : "Registrar movimiento"}</button>
      </form>

      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A" }}>Últimos movimientos</div>
        {canManage && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <ExportButtons kind="vehicles" rows={list} testIdPrefix="veh" />
            <ClearLogsButton endpoint="/vehicles" clients={clients} label="vehículos" onDone={load} testidPrefix="veh" />
          </div>
        )}
      </div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {list.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin movimientos registrados.</div>}
        {list.slice(0, 30).map((v) => (
          <div key={v.id} onClick={() => setDetail(v)} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} data-testid={`veh-row-${v.id}`}>
            {v.plate_photo_url ? (
              <img src={fileSrc(v.plate_photo_url)} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}/>
            ) : (
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: v.direction === "entrada" ? "#16a34a" : "#BF3437", color: "white", display: "grid", placeItems: "center", flexShrink: 0 }}>
                {v.direction === "entrada" ? <ArrowDown size={14}/> : <ArrowUp size={14}/>}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 13, fontFamily: "monospace", letterSpacing: "0.1em" }}>
                {v.plate} <span className={`badge ${v.direction === "entrada" ? "green" : "red"}`} style={{ marginLeft: 6 }}>{v.direction}</span>
                {v.plate_source === "ocr" && (
                  <span data-testid={`veh-plate-source-${v.id}`} title="Patente leída automáticamente por IA" style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: "#16a34a", border: "1px solid #16a34a", borderRadius: 2, padding: "1px 4px", letterSpacing: "0.05em" }}>
                    <ScanLine size={9} style={{ display: "inline", verticalAlign: "-1px", marginRight: 2 }}/>OCR
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{v.client_name} · {new Date(v.created_at).toLocaleString("es-AR")}</div>
              {(v.driver_name || v.driver_surname) && <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Conductor: {[v.driver_surname, v.driver_name].filter(Boolean).join(", ")}{v.driver_dni ? ` · DNI ${v.driver_dni}` : ""}</div>}
              {v.description && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{v.description}</div>}
              {toolsSummary(v.tools_check) && <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Herramientas: {toolsSummary(v.tools_check)}</div>}
            </div>
            {canManage && (
              <button type="button" onClick={(e) => { e.stopPropagation(); removeLog(v.id); }} title="Eliminar movimiento"
                style={{ padding: 7, border: "1px solid var(--invict-bone-3)", background: "white", color: "#BF3437", borderRadius: 2, cursor: "pointer", flexShrink: 0 }}
                data-testid={`veh-delete-${v.id}`}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
      <VehicleDetailModal vehicle={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
